// @ts-nocheck
import React, { useState } from 'react';

interface CollectionItem {
  _id: string;
  name: string;
}

interface AddLinkModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: { url: string; note: string; tags: string[]; collectionIds: string[] }) => Promise<any>;
  collections: CollectionItem[];
}

export default function AddLinkModal({
  isOpen,
  onClose,
  onSubmit,
  collections = [],
}: AddLinkModalProps) {
  const [url, setUrl] = useState('');
  const [note, setNote] = useState('');
  const [tagInput, setTagInput] = useState('');
  const [selectedCollection, setSelectedCollection] = useState('');
  const [statusMessage, setStatusMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  if (!isOpen) return null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!url.trim()) {
      setErrorMessage('Please enter a valid URL');
      return;
    }

    setErrorMessage('');
    setIsSubmitting(true);
    setStatusMessage('Saving link…');

    try {
      setTimeout(() => {
        if (isSubmitting) setStatusMessage('Fetching preview…');
      }, 700);

      const tags = tagInput
        .split(',')
        .map((t) => t.trim().toLowerCase())
        .filter(Boolean);

      const result = await onSubmit({
        url: url.trim(),
        note: note.trim(),
        tags,
        collectionIds: selectedCollection ? [selectedCollection] : [],
      });

      setStatusMessage('Saved to library');
      setTimeout(() => {
        setUrl('');
        setNote('');
        setTagInput('');
        setSelectedCollection('');
        setStatusMessage('');
        setIsSubmitting(false);
        onClose();
      }, 600);
    } catch (err: any) {
      setIsSubmitting(false);
      setStatusMessage('');
      setErrorMessage(err.message || 'Failed to save link. Please check the URL.');
    }
  };

  return (
    <div className="reader-modal-overlay" onClick={onClose} role="dialog" aria-modal="true">
      <div className="reader-modal-card" onClick={(e) => e.stopPropagation()}>
        <div className="reader-modal-header">
          <h3 className="reader-modal-title">Save Link to Library</h3>
          <button
            onClick={onClose}
            style={{ background: 'none', border: 'none', fontSize: '1.2rem', cursor: 'pointer', color: 'var(--reader-text-muted)' }}
          >
            ×
          </button>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="reader-modal-body">
            {errorMessage && (
              <div
                style={{
                  padding: '10px 12px',
                  background: 'rgba(239, 68, 68, 0.1)',
                  border: '1px solid #ef4444',
                  borderRadius: '4px',
                  color: '#ef4444',
                  fontSize: '0.85rem',
                }}
              >
                {errorMessage}
              </div>
            )}

            {statusMessage && (
              <div
                style={{
                  padding: '10px 12px',
                  background: 'var(--reader-accent-light)',
                  border: '1px solid var(--reader-accent)',
                  borderRadius: '4px',
                  color: 'var(--reader-accent)',
                  fontSize: '0.85rem',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                }}
              >
                <i className="fi fi-rr-spinner" style={{ animation: 'spin 1s linear infinite' }} />
                <span>{statusMessage}</span>
              </div>
            )}

            <div className="reader-form-group">
              <label className="reader-form-label">URL *</label>
              <input
                type="url"
                className="reader-input"
                placeholder="https://example.com/article, youtube.com, podcast..."
                value={url}
                onChange={(e) => setUrl(e.target.value)}
                autoFocus
                required
                disabled={isSubmitting}
              />
              <span style={{ fontSize: '0.75rem', color: 'var(--reader-text-subtle)' }}>
                Supports articles, YouTube videos, podcast episodes, tweets, and blog posts.
              </span>
            </div>

            <div className="reader-form-group">
              <label className="reader-form-label">Tags (comma-separated)</label>
              <input
                type="text"
                className="reader-input"
                placeholder="tech, ai, finance, productivity"
                value={tagInput}
                onChange={(e) => setTagInput(e.target.value)}
                disabled={isSubmitting}
              />
            </div>

            <div className="reader-form-group">
              <label className="reader-form-label">Collection</label>
              <select
                className="reader-input"
                value={selectedCollection}
                onChange={(e) => setSelectedCollection(e.target.value)}
                disabled={isSubmitting}
              >
                <option value="">No collection</option>
                {collections.map((col) => (
                  <option key={col._id} value={col._id}>
                    {col.name}
                  </option>
                ))}
              </select>
            </div>

            <div className="reader-form-group">
              <label className="reader-form-label">Note / Takeaway (optional)</label>
              <textarea
                className="reader-input"
                style={{ height: '70px', padding: '8px 12px' }}
                placeholder="Why are you saving this? Key summary..."
                value={note}
                onChange={(e) => setNote(e.target.value)}
                disabled={isSubmitting}
              />
            </div>
          </div>

          <div className="reader-modal-footer">
            <button type="button" className="reader-btn" onClick={onClose} disabled={isSubmitting}>
              Cancel
            </button>
            <button type="submit" className="reader-btn reader-btn-primary" disabled={isSubmitting || !url.trim()}>
              {isSubmitting ? 'Saving…' : 'Save to Library'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
